interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function zonedParts(value: Date, timeZone: string): ZonedParts {
  const values = new Map(
    new Intl.DateTimeFormat('en-NZ', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(value)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  );
  return {
    year: values.get('year') ?? Number.NaN,
    month: values.get('month') ?? Number.NaN,
    day: values.get('day') ?? Number.NaN,
    hour: values.get('hour') ?? Number.NaN,
    minute: values.get('minute') ?? Number.NaN,
    second: values.get('second') ?? Number.NaN,
  };
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function dateTimeLocalValue(value: string | null, timeZone: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = zonedParts(date, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(
    parts.minute,
  )}`;
}

export function zonedDateTimeToIso(value: string, timeZone: string): string {
  const match =
    /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2})(?::(?<second>\d{2}))?$/u.exec(
      value,
    );
  if (!match?.groups) throw new Error('Enter a valid local date and time.');
  const requested: ZonedParts = {
    year: Number(match.groups.year),
    month: Number(match.groups.month),
    day: Number(match.groups.day),
    hour: Number(match.groups.hour),
    minute: Number(match.groups.minute),
    second: Number(match.groups.second ?? 0),
  };
  const requestedWallTime = Date.UTC(
    requested.year,
    requested.month - 1,
    requested.day,
    requested.hour,
    requested.minute,
    requested.second,
  );
  const normalized = new Date(requestedWallTime);
  if (
    normalized.getUTCFullYear() !== requested.year ||
    normalized.getUTCMonth() + 1 !== requested.month ||
    normalized.getUTCDate() !== requested.day ||
    normalized.getUTCHours() !== requested.hour ||
    normalized.getUTCMinutes() !== requested.minute ||
    normalized.getUTCSeconds() !== requested.second
  ) {
    throw new Error('Enter a valid local date and time.');
  }

  let instant = requestedWallTime;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const observed = zonedParts(new Date(instant), timeZone);
    const observedWallTime = Date.UTC(
      observed.year,
      observed.month - 1,
      observed.day,
      observed.hour,
      observed.minute,
      observed.second,
    );
    const difference = requestedWallTime - observedWallTime;
    instant += difference;
    if (difference === 0) break;
  }
  const resolved = zonedParts(new Date(instant), timeZone);
  if (
    resolved.year !== requested.year ||
    resolved.month !== requested.month ||
    resolved.day !== requested.day ||
    resolved.hour !== requested.hour ||
    resolved.minute !== requested.minute ||
    resolved.second !== requested.second
  ) {
    throw new Error(`That local time does not exist in ${timeZone}.`);
  }
  return new Date(instant).toISOString();
}

export function nextQuarterHourValue(timeZone: string, now = Date.now()): string {
  const quarterHour = 15 * 60 * 1000;
  const next = Math.ceil((now + quarterHour) / quarterHour) * quarterHour;
  return dateTimeLocalValue(new Date(next).toISOString(), timeZone);
}
